import { useEffect, useState, MouseEvent, useMemo, useCallback } from "react";
import { useRouter } from "next/router";
import { Accordion, Button, Tooltip } from "@heathmont/moon-core-tw";
import { ControlsEye } from "@heathmont/moon-icons-tw";
import { useAuth } from "@/contexts/auth/AuthContext";
import { useNavigator } from "@/contexts/navigator/NavigatorContext";
import { useEntities } from "@/contexts/entities/EntitiesContext";
import { useSavedResources } from "@/contexts/savedResources/SavedResourcesContext";
import Analytics, { ClickActions } from "@/lib/analytics";
import { SubCompetency } from "@/lib/models";
import {
  createShareUrlWithSelectionQueryParam,
  getPerformanceLevelHierarchyFromId,
  getSelectionQueryParamFromRouter,
  sortSubCompetencyById,
} from "@/lib/utils";
import ChevronDown from "../icons/ChevronDown";
import { HtmlRenderer } from "../HtmlRenderer";
import BookmarkIcon from "../icons/Bookmark";
import BookmarkCheckedIcon from "../icons/BookmarkChecked";
import Share from "../icons/Share";
import {
  ResourcePath,
  SavedResourcesAction,
  SavedResourcesToast,
} from "./SavedResourcesToast";
import { ShareToast } from "./ShareToast";
import { ErrorToast } from "./ErrorToast";
import {
  ButtonSizes,
  ButtonTypes,
  IconTypes,
  StyledButton,
} from "@/components/StyledButton";
import { ProgressionArrow } from "../icons/ProgressionArrow";
import { ComponentSkillsAccordionToggleButton } from "../account/ComponentSkillsAccordionToggleButton";
import { SavedResourcesLocation } from "@/lib/analytics/savedResources";

type SubCompetencyListProps = {
  subCompetencies: SubCompetency[];
  competencyName: string;
};

type SavedResourcesToastState = {
  isOpen: boolean;
  currentAction: SavedResourcesAction | null;
};

export const SubCompetencyList = ({
  subCompetencies,
  competencyName,
}: SubCompetencyListProps) => {
  const { user, openLoginPrompt } = useAuth();
  const router = useRouter();
  const {
    activeSubCompetencyId,
    activePerformanceLevelId,
    activeLearnerOutcomeId,
  } = useNavigator();
  const { getEntityHierarchyFromId } = useEntities();
  const { addComponentSkill, removeComponentSkill, componentSkills } =
    useSavedResources();

  const [savedResourcesToastState, setSavedResourcesToastState] =
    useState<SavedResourcesToastState>({
      isOpen: false,
      currentAction: null,
    });
  const [isErrorToastOpen, setIsErrorToastOpen] = useState<boolean>(false);
  const [hasLinkCopied, setHasLinkCopied] = useState(false);
  const [selectedAccordions, setSelectedAccordions] = useState<string[]>([]);

  const isComponentSkillSaved = (id: string) =>
    componentSkills.find((skill) => skill.id === id);

  const areAllAccordionsOpen = useMemo(() => {
    return selectedAccordions.length === subCompetencies.length;
  }, [selectedAccordions, subCompetencies]);

  const onToggleSelectedAccordion = (id: string) => {
    const isSelected = selectedAccordions.includes(id);
    if (isSelected) {
      setSelectedAccordions((prevSelections) =>
        prevSelections.filter((x) => x !== id),
      );
      Analytics.handleSubCompetencyClick({
        entityHierarchy: getEntityHierarchyFromId(id),
        action: ClickActions.Collapse,
      });
    } else {
      setSelectedAccordions((prevSelections) => [...prevSelections, id]);
      Analytics.handleSubCompetencyClick({
        entityHierarchy: getEntityHierarchyFromId(id),
        action: ClickActions.Expand,
      });
    }
  };

  const onSelectedAccordionsChange = (selectedAccordions: string[]) => {
    setSelectedAccordions((prevSelections) => {
      const closedSubCompetencies = prevSelections.filter(
        (x) => !selectedAccordions.includes(x),
      );
      closedSubCompetencies.forEach((subCompetencyId) =>
        Analytics.handleSubCompetencyClick({
          entityHierarchy: getEntityHierarchyFromId(subCompetencyId),
          action: ClickActions.Collapse,
        }),
      );

      const openedSubCompetencies = selectedAccordions.filter(
        (x) => !prevSelections.includes(x),
      );

      openedSubCompetencies.forEach((subCompetencyId) =>
        Analytics.handleSubCompetencyClick({
          entityHierarchy: getEntityHierarchyFromId(subCompetencyId),
          action: ClickActions.Expand,
        }),
      );

      return selectedAccordions;
    });
  };

  const toggleAllAccordions = () => {
    if (areAllAccordionsOpen) {
      Analytics.handleSubCompetencyClick({
        entityHierarchy: getEntityHierarchyFromId(
          getSelectionQueryParamFromRouter(router) as string,
        ),
        action: ClickActions.Collapse,
      });
      setSelectedAccordions([]);
    } else {
      Analytics.handleSubCompetencyClick({
        entityHierarchy: getEntityHierarchyFromId(
          getSelectionQueryParamFromRouter(router) as string,
        ),
        action: ClickActions.Expand,
      });
      setSelectedAccordions(
        subCompetencies.map((subCompetency) => subCompetency.id),
      );
    }
  };

  const handleClick = (id: string) => async (e: MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      openLoginPrompt("component_skill");
      return;
    }
    try {
      if (isComponentSkillSaved(id)) {
        await removeComponentSkill(id);
        setSavedResourcesToastState({
          isOpen: true,
          currentAction: SavedResourcesAction.Remove,
        });
        Analytics.handleRemoveFromSavedResourcesEvent(
          getEntityHierarchyFromId(id),
          SavedResourcesLocation.SubCompetencyList,
        );
      } else {
        await addComponentSkill(id);
        setSavedResourcesToastState({
          isOpen: true,
          currentAction: SavedResourcesAction.Add,
        });
        Analytics.handleAddToSavedResourcesEvent(
          getEntityHierarchyFromId(id),
          SavedResourcesLocation.SubCompetencyList,
        );
      }
    } catch (e) {
      console.log(e);
      setIsErrorToastOpen(true);
    }
  };

  const handleCopyLinkClick = (id: string) => (e: MouseEvent) => {
    e.preventDefault();
    Analytics.handleShareLinkClick(getEntityHierarchyFromId(id));
    navigator.clipboard.writeText(createShareUrlWithSelectionQueryParam(id));
    setHasLinkCopied(true);
  };

  const handleCloseToast = useCallback(() => {
    setSavedResourcesToastState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleExternalResourceLinkClick = (id: string, url: string) => () => {
    Analytics.handleExternalResourceLinkClick({
      entityHierarchy: getEntityHierarchyFromId(id),
      url,
    });
  };

  useEffect(() => {
    if (activeSubCompetencyId) {
      setSelectedAccordions([activeSubCompetencyId]);
      const anchor = document.getElementById(activeSubCompetencyId);
      if (anchor) {
        setTimeout(() => {
          anchor.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 200);
      }
    }
  }, [activeSubCompetencyId]);

  return (
    <section>
      <header className="flex flex-col pb-2.5 small:justify-between small:pb-[18px] medium:flex-col">
        <div>
          <h3 className="mb-4 font-roobert text-2xl font-normal text-black">
            Component Skills
          </h3>
          <div className="flex items-center justify-between">
            {!!competencyName && subCompetencies.length > 0 && (
              <div className="font-roobert text-base font-normal leading-tight text-neutral-600">
                {competencyName} is comprised of the following{" "}
                {subCompetencies.length} component skills, each with a learning
                progression for developing proficiency.
              </div>
            )}
            {subCompetencies.length > 0 && (
              <div className="hidden shrink-0 medium:flex">
                <ComponentSkillsAccordionToggleButton
                  shouldCollapse={areAllAccordionsOpen}
                  handleClick={toggleAllAccordions}
                />
              </div>
            )}
          </div>
        </div>
      </header>

      <Accordion
        value={selectedAccordions}
        onValueChange={onSelectedAccordionsChange}
      >
        {subCompetencies.sort(sortSubCompetencyById).map((subCompetency) => (
          <Accordion.Item
            key={subCompetency.id}
            value={subCompetency.id}
            className="group rounded-xl border bg-white px-2 py-0"
          >
            <div
              className="flex scroll-mt-[100px] justify-between p-3 py-0 pr-2"
              id={subCompetency.id}
            >
              <div
                className="flex w-full items-center py-5"
                onClick={() => onToggleSelectedAccordion(subCompetency.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggleSelectedAccordion(subCompetency.id);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <span
                  className={`mr-4 flex h-[25px] w-[25px] items-center justify-center rounded-full bg-${activeLearnerOutcomeId}-30 shrink-0 capitalize text-black`}
                >
                  {subCompetency.id.at(subCompetency.id.length - 1)}
                </span>
                <h4 className="font-roobert text-lg font-medium">
                  {subCompetency.name}
                </h4>
              </div>
              <div className="flex items-center justify-end">
                <Tooltip>
                  <Tooltip.Trigger className="mr-2 flex items-center small:mr-4">
                    <StyledButton
                      buttonType={ButtonTypes.Tertiary}
                      size={ButtonSizes.Small}
                      iconType={IconTypes.Only}
                      onClick={handleCopyLinkClick(subCompetency.id)}
                      aria-label="Copy Component Skill link to clipboard"
                    >
                      <Share fill="#145758" />
                    </StyledButton>
                  </Tooltip.Trigger>
                  <Tooltip.Content className="z-10 bg-black text-white">
                    Copy Component Skill link to clipboard
                    <Tooltip.Arrow className="z-10 bg-black" />
                  </Tooltip.Content>
                </Tooltip>

                <Tooltip>
                  <Tooltip.Trigger className="mr-2 flex items-center small:mr-4">
                    <StyledButton
                      buttonType={ButtonTypes.Tertiary}
                      size={ButtonSizes.Small}
                      iconType={IconTypes.Only}
                      onClick={handleClick(subCompetency.id)}
                      aria-label={
                        isComponentSkillSaved(subCompetency.id)
                          ? "Remove Component Skill from Saved Resources"
                          : "Add Component Skill to Saved Resources"
                      }
                    >
                      {isComponentSkillSaved(subCompetency.id) ? (
                        <BookmarkCheckedIcon />
                      ) : (
                        <BookmarkIcon className="m-auto" />
                      )}
                    </StyledButton>
                  </Tooltip.Trigger>
                  <Tooltip.Content className="z-10 bg-black text-white">
                    {isComponentSkillSaved(subCompetency.id)
                      ? "Saved to Resources"
                      : "Save to Resources"}

                    <Tooltip.Arrow className="z-10 bg-black" />
                  </Tooltip.Content>
                </Tooltip>
                <Accordion.Button className="w-auto flex-none rounded-[100px] !p-1 hover:bg-tab-bg-hover active:bg-tab-bg-press">
                  <ChevronDown
                    className="transition duration-200 moon-open:rotate-180"
                    aria-label={`Toggle accordion for ${subCompetency.name}`}
                  />
                </Accordion.Button>
              </div>
            </div>

            <Accordion.Content className="overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown">
              <div className="flex flex-col pb-2" role="grid">
                {!!subCompetency.description && (
                  <div className="mb-8 leading-5 text-dark-color">
                    <HtmlRenderer html={subCompetency.description} />
                  </div>
                )}
                <PerformanceLevels
                  subCompetency={subCompetency}
                  activePerformanceLevel={activePerformanceLevelId}
                />
                {subCompetency.externalResourcesUrl && (
                  <Button
                    as="a"
                    target="_blank"
                    href={subCompetency.externalResourcesUrl}
                    onClick={handleExternalResourceLinkClick(
                      subCompetency.id,
                      subCompetency.externalResourcesUrl,
                    )}
                    className="mt-4 self-end rounded-sm bg-black px-5 py-2 font-bold text-white shadow-moon-sm"
                    aria-label="explore resources"
                  >
                    <ControlsEye width={24} height={24} />
                    Explore Resources
                  </Button>
                )}
              </div>
            </Accordion.Content>
          </Accordion.Item>
        ))}
      </Accordion>

      <ShareToast
        isOpen={hasLinkCopied}
        handleClose={() => setHasLinkCopied((prevState) => !prevState)}
      />
      <SavedResourcesToast
        isOpen={savedResourcesToastState.isOpen}
        currentAction={savedResourcesToastState.currentAction}
        handleClose={handleCloseToast}
        resourcePath={ResourcePath.ComponentSkill}
      />
      <ErrorToast
        isOpen={isErrorToastOpen}
        handleClose={() => setIsErrorToastOpen(false)}
      />
    </section>
  );
};

type PerformanceLevelsProps = {
  subCompetency: SubCompetency;
  activePerformanceLevel: string | null;
};

const PerformanceLevels = ({
  subCompetency,
  activePerformanceLevel,
}: PerformanceLevelsProps) => {
  const performanceLevels = [
    {
      id: subCompetency.performanceLevel1Id,
      description: subCompetency.performanceLevel1Description,
      name: getPerformanceLevelHierarchyFromId(
        subCompetency.performanceLevel1Id,
      ),
    },
    {
      id: subCompetency.performanceLevel2Id,
      description: subCompetency.performanceLevel2Description,
      name: getPerformanceLevelHierarchyFromId(
        subCompetency.performanceLevel2Id,
      ),
    },
    {
      id: subCompetency.performanceLevel3Id,
      description: subCompetency.performanceLevel3Description,
      name: getPerformanceLevelHierarchyFromId(
        subCompetency.performanceLevel3Id,
      ),
    },
    {
      id: subCompetency.performanceLevel4Id,
      description: subCompetency.performanceLevel4Description,
      name: getPerformanceLevelHierarchyFromId(
        subCompetency.performanceLevel4Id,
      ),
    },
  ];

  return (
    <div
      className="grid w-full grid-cols-1 gap-3 medium:grid-cols-4"
      role="row"
    >
      {performanceLevels.map((performanceLevel) => (
        <div
          key={performanceLevel.id}
          className="leading-5 aria-selected:border aria-selected:border-green-500 aria-selected:bg-green-50 medium:[&:not(:last-of-type)_svg]:inline"
          role="gridcell"
          aria-selected={performanceLevel.id === activePerformanceLevel}
          aria-label={performanceLevel.id}
        >
          <h5 className="flex items-center font-roobert text-base font-semibold medium:mb-2.5">
            {performanceLevel.name}
            <ProgressionArrow className="hidden pl-3 medium:relative" />
          </h5>
          <p className="font-roobert text-base font-normal text-gray-text">
            {performanceLevel.description}
          </p>
        </div>
      ))}
    </div>
  );
};
